import React from "react";

/**
 * Animated MOSAIC Mascot Logo
 * Character-only PNG with crisp HTML text rendered separately.
 */

const SIZES = {
  small:  { img: 40, wrapper: 40 },
  medium: { img: 160, wrapper: 160 },
  large:  { img: 140, wrapper: 140 },
};

const MOSAIC_LETTERS = [
  { letter: "M", color: "#EF4444" },
  { letter: "O", color: "#F59E0B" },
  { letter: "S", color: "#10B981" },
  { letter: "A", color: "#F59E0B" },
  { letter: "I", color: "#0EA5E9" },
  { letter: "C", color: "#8B5CF6" },
];

function MosaicLogo({ size = "large", className = "" }) {
  const dims = SIZES[size] || SIZES.large;

  return (
    <div
      className={`mosaic-mascot ${className}`}
      style={{ width: dims.wrapper, height: dims.wrapper, flexShrink: 0 }}
      data-testid="mosaic-animated-logo"
    >
      <img
        src="/mosaic-mascot.png"
        alt="MOSAIC Mascot"
        style={{ width: dims.img, height: dims.img, objectFit: "contain" }}
        draggable={false}
      />
      <style>{`
        .mosaic-mascot {
          display: flex;
          align-items: center;
          justify-content: center;
          animation: mascotFloat 3s ease-in-out infinite;
          cursor: pointer;
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .mosaic-mascot:hover {
          animation: mascotBounce 0.6s ease;
          transform: scale(1.08);
        }
        .mosaic-mascot img {
          filter: drop-shadow(0 2px 6px rgba(0,47,167,0.18));
          transition: filter 0.3s ease;
        }
        .mosaic-mascot:hover img {
          filter: drop-shadow(0 4px 12px rgba(0,47,167,0.28));
        }
        @keyframes mascotFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes mascotBounce {
          0% { transform: scale(1); }
          30% { transform: scale(1.12) rotate(-2deg); }
          50% { transform: scale(0.95) rotate(1deg); }
          70% { transform: scale(1.06); }
          100% { transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
}

function MosaicWordmark({ size = "lg", light = false }) {
  const textSize = size === "xl" ? "text-3xl" : size === "lg" ? "text-2xl" : size === "md" ? "text-xl" : "text-lg";
  const subSize = size === "xl" ? "text-sm" : size === "lg" ? "text-xs" : "text-[10px]";
  const subColor = light ? "text-blue-200/80" : "text-gray-500";

  return (
    <div className="text-center">
      <div className={`font-black ${textSize} tracking-wider`} style={{ fontFamily: "Outfit, sans-serif" }}>
        {MOSAIC_LETTERS.map(({ letter, color }, i) => (
          <span key={i} style={{ color }}>{letter}</span>
        ))}
      </div>
      <div className={`${subSize} ${subColor} font-medium mt-0.5`}>
        Multilingual Student Assessment
      </div>
    </div>
  );
}

// Full logo — mascot + colorful wordmark (login page, guide)
export function MosaicLogoFull({ className = "" }) {
  return (
    <div className={`flex flex-col items-center gap-1 ${className}`} data-testid="mosaic-logo-full">
      <MosaicLogo size="large" />
      <MosaicWordmark size="xl" />
    </div>
  );
}

// Compact version for navbar
export function MosaicLogoNav({ className = "" }) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`} data-testid="mosaic-logo-nav">
      <MosaicLogo size="small" />
      <div>
        <div className="font-black text-lg leading-tight tracking-wider" style={{ fontFamily: "Outfit, sans-serif" }}>
          {MOSAIC_LETTERS.map(({ letter, color }, i) => (
            <span key={i} style={{ color }}>{letter}</span>
          ))}
        </div>
        <div className="text-[10px] text-gray-500 leading-tight">Mosaic Language Proficiency Assessment</div>
      </div>
    </div>
  );
}

// Home hero version — mascot + white/light text
export function MosaicLogoHero({ className = "" }) {
  return (
    <div className={`flex items-center gap-4 ${className}`} data-testid="mosaic-logo-hero">
      <MosaicLogo size="medium" />
      <MosaicWordmark size="xl" light />
    </div>
  );
}

export default MosaicLogo;
